import React, { useEffect, useState, useRef } from 'react';
import { initializeModal } from '../../utils/ModalUtils';
import { updateTemplateDefinition, reviewCondition } from '../../services/StrategyTemplatesService';
import { processError } from '../../services/ServiceUtils';

const RISK_BADGE = { low: 'bg-success', medium: 'bg-warning text-dark', high: 'bg-danger' };
const RISK_LABEL = { low: 'Baixo risco', medium: 'Atenção', high: 'Alto risco' };

// Monta o texto de placeholder "${nome}" sem literal "${" no fonte (evita o lint no-template-curly-in-string).
const PH = (k) => String.fromCharCode(36) + '{' + k + '}';

const LEVELS = [
    { key: 'CONSERVATIVE', label: '🛡️ Conservador' },
    { key: 'MODERATE', label: '⚖️ Moderado' },
    { key: 'AGGRESSIVE', label: '🚀 Agressivo' },
];

/**
 * Edita a camada DECLARATIVA de um template (valores/presets/labels/hints/enabled). Não mexe na lógica
 * (builders ficam em código). A validação real é no backend (chaves/tipos travados). props: data, onSaved.
 */
function StrategyTemplateModal(props) {

    const btnClose = useRef('');
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [reviews, setReviews] = useState({}); // { [roleKey]: { loading, result, error } }

    function onReviewCondition(role) {
        const condition = (form.conditionTemplates && form.conditionTemplates[role.key]) || '';
        if (!condition.trim()) { setReviews(r => ({ ...r, [role.key]: { result: { risk: 'low', findings: [], summary: 'Vazio = usa o padrão do código.' } } })); return; }
        setReviews(r => ({ ...r, [role.key]: { loading: true } }));
        reviewCondition({ role: role.key, condition, templateName: form.name })
            .then(result => setReviews(r => ({ ...r, [role.key]: { result } })))
            .catch(err => setReviews(r => ({ ...r, [role.key]: { error: processError(err) } })));
    }

    useEffect(() => {
        if (!props.data) return;
        // cópia editável profunda (presets/configFields)
        setForm(JSON.parse(JSON.stringify(props.data)));
        setErrors([]);
    }, [props.data]);

    useEffect(() => {
        initializeModal('modalStrategyTemplate', () => { setErrors([]); });
    }, []);

    // Esqueleto SEMPRE com modal-dialog/modal-content válidos — o Bootstrap abre o modal no clique do
    // botão (data-bs-toggle) ANTES do React preencher o form; sem o .modal-dialog ele estoura
    // "Illegal invocation" no _showElement. Enquanto form==null, mostra "Carregando…".
    if (!form) return (
        <div className="modal fade" id="modalStrategyTemplate" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-body text-center text-muted py-4">Carregando…</div>
                </div>
            </div>
        </div>
    );

    function setTop(prop, value) {
        setForm(f => ({ ...f, [prop]: value }));
    }
    function setFieldProp(idx, prop, value) {
        setForm(f => {
            const cf = f.configFields.slice();
            cf[idx] = { ...cf[idx], [prop]: value };
            return { ...f, configFields: cf };
        });
    }
    function setConditionTpl(role, value) {
        setForm(f => ({ ...f, conditionTemplates: { ...(f.conditionTemplates || {}), [role]: value } }));
    }
    function setPreset(level, key, value) {
        setForm(f => {
            const presets = { ...(f.presets || {}) };
            presets[level] = { ...(presets[level] || {}) };
            if (value === '') delete presets[level][key];
            else presets[level][key] = value;
            return { ...f, presets };
        });
    }

    function buildPayload() {
        const byKey = Object.fromEntries((form.configFields || []).map(f => [f.key, f]));
        const presets = {};
        for (const lvl of LEVELS) {
            const p = (form.presets && form.presets[lvl.key]) || {};
            const clean = {};
            for (const k of Object.keys(p)) {
                if (p[k] === '' || p[k] === null || p[k] === undefined) continue;
                clean[k] = byKey[k] && byKey[k].type === 'number' ? Number(p[k]) : p[k];
            }
            if (Object.keys(clean).length) presets[lvl.key] = clean;
        }
        const configFields = (form.configFields || []).map(f => ({
            ...f,
            default: f.type === 'number' && f.default !== '' && f.default !== undefined ? Number(f.default) : f.default,
        }));
        const payload = {
            name: form.name,
            description: form.description,
            difficulty: form.difficulty,
            enabled: !!form.enabled,
            sortOrder: Number(form.sortOrder) || 0,
            configFields,
            presets,
        };
        // Lógica-no-DB: só envia conditionTemplates quando o template aceita (logicEditable).
        if (form.logicEditable) payload.conditionTemplates = form.conditionTemplates || {};
        return payload;
    }

    function onSave() {
        setSaving(true);
        setErrors([]);
        updateTemplateDefinition(form.id, buildPayload())
            .then(() => { setSaving(false); if (btnClose.current) btnClose.current.click(); if (props.onSaved) props.onSaved(); })
            .catch(err => {
                setSaving(false);
                const data = err && err.response && err.response.data;
                if (data && Array.isArray(data.errors)) setErrors(data.errors);
                else setErrors([processError(err)]);
            });
    }

    return (
        <div className="modal fade" id="modalStrategyTemplate" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">Editar Template — {form.name}</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">

                        <div className="row">
                            <div className="col-md-8 mb-3">
                                <label htmlFor="tplName">Nome</label>
                                <input className="form-control" id="tplName" type="text" value={form.name || ''}
                                    onChange={e => setTop('name', e.target.value)} />
                            </div>
                            <div className="col-md-2 mb-3">
                                <label htmlFor="tplOrder">Ordem</label>
                                <input className="form-control" id="tplOrder" type="number" value={form.sortOrder ?? 0}
                                    onChange={e => setTop('sortOrder', e.target.value)} />
                            </div>
                            <div className="col-md-2 mb-3 d-flex align-items-end">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="tplEnabled"
                                        checked={!!form.enabled} onChange={e => setTop('enabled', e.target.checked)} />
                                    <label className="form-check-label" htmlFor="tplEnabled">Ativo</label>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="tplDesc">Descrição</label>
                            <textarea className="form-control" id="tplDesc" rows="2" value={form.description || ''}
                                onChange={e => setTop('description', e.target.value)} />
                        </div>

                        <hr />
                        <h6>Campos (rótulo, dica e valor padrão)</h6>
                        <small className="text-muted d-block mb-2">A chave e o tipo do campo são fixos (definidos no código). Aqui você ajusta só rótulo, dica, padrão e limites.</small>
                        {(form.configFields || []).map((f, idx) => (
                            <div className="row align-items-center mb-2" key={f.key}>
                                <div className="col-md-3"><code>{f.key}</code> <small className="text-muted">({f.type})</small></div>
                                <div className="col-md-3">
                                    <input className="form-control form-control-sm" placeholder="rótulo" value={f.label || ''}
                                        onChange={e => setFieldProp(idx, 'label', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <input className="form-control form-control-sm" placeholder="dica" value={f.hint || ''}
                                        onChange={e => setFieldProp(idx, 'hint', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    {f.type === 'select'
                                        ? <select className="form-select form-select-sm" value={f.default ?? ''} onChange={e => setFieldProp(idx, 'default', e.target.value)}>
                                            {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                                          </select>
                                        : <input className="form-control form-control-sm" type="number" placeholder="padrão" value={f.default ?? ''}
                                            onChange={e => setFieldProp(idx, 'default', e.target.value)} />}
                                </div>
                            </div>
                        ))}

                        {form.presets && (
                            <React.Fragment>
                                <hr />
                                <h6>Presets (🛡️ / ⚖️ / 🚀)</h6>
                                <small className="text-muted d-block mb-2">Valores pré-prontos por nível. Deixe em branco para o preset não definir aquele campo.</small>
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th>Campo</th>
                                                {LEVELS.map(l => <th key={l.key}>{l.label}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(form.configFields || []).map(f => (
                                                <tr key={f.key}>
                                                    <td><code>{f.key}</code></td>
                                                    {LEVELS.map(l => {
                                                        const val = (form.presets[l.key] && form.presets[l.key][f.key] !== undefined) ? form.presets[l.key][f.key] : '';
                                                        return (
                                                            <td key={l.key}>
                                                                {f.type === 'select'
                                                                    ? <select className="form-select form-select-sm" value={val} onChange={e => setPreset(l.key, f.key, e.target.value)}>
                                                                        <option value="">—</option>
                                                                        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                                                                      </select>
                                                                    : <input className="form-control form-control-sm" type="number" value={val}
                                                                        onChange={e => setPreset(l.key, f.key, e.target.value)} />}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </React.Fragment>
                        )}

                        {form.logicEditable && (
                            <React.Fragment>
                                <hr />
                                <h6>Condições (lógica avançada) ⚠️</h6>
                                <small className="text-muted d-block mb-2">
                                    Edita a lógica de compra/venda sem deploy. Placeholders no formato cifrão+chaves: <code>{PH('symbol')}</code>, <code>{PH('interval')}</code>, <code>{PH('userId')}</code> e os campos de config. <strong>Vazio = usa o padrão do código.</strong> Validado contra injeção ao salvar; muda só estratégias criadas a partir de agora.
                                </small>
                                {(form.conditionRoles && form.conditionRoles.length
                                    ? form.conditionRoles
                                    : [{ key: 'buy', label: 'Compra' }, { key: 'sell', label: 'Venda' }]
                                ).map(role => (
                                    <div className="mb-2" key={role.key}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label mb-0">Condição de {role.label}</label>
                                            <button type="button" className="btn btn-outline-info btn-xs"
                                                disabled={reviews[role.key] && reviews[role.key].loading}
                                                onClick={() => onReviewCondition(role)}>
                                                {reviews[role.key] && reviews[role.key].loading ? 'Revisando…' : '🧠 Revisar com IA'}
                                            </button>
                                        </div>
                                        <textarea className="form-control form-control-sm font-monospace" rows="2"
                                            placeholder="(vazio = padrão do código)"
                                            value={(form.conditionTemplates && form.conditionTemplates[role.key]) || ''}
                                            onChange={e => setConditionTpl(role.key, e.target.value)} />
                                        {reviews[role.key] && reviews[role.key].error && (
                                            <small className="text-danger d-block mt-1">{reviews[role.key].error}</small>
                                        )}
                                        {reviews[role.key] && reviews[role.key].result && (
                                            <div className="small mt-1">
                                                <span className={`badge ${RISK_BADGE[reviews[role.key].result.risk] || 'bg-secondary'}`}>
                                                    {RISK_LABEL[reviews[role.key].result.risk] || reviews[role.key].result.risk}
                                                </span>
                                                {reviews[role.key].result._servedBy && <span className="text-muted ms-2">via {reviews[role.key].result._servedBy}</span>}
                                                {reviews[role.key].result.summary && <div className="text-muted mt-1">{reviews[role.key].result.summary}</div>}
                                                {(reviews[role.key].result.findings || []).length > 0 && (
                                                    <ul className="mb-0 mt-1 ps-3">
                                                        {reviews[role.key].result.findings.map((f, i) => <li key={i}>{f}</li>)}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </React.Fragment>
                        )}

                        {errors.length > 0 && (
                            <div className="alert alert-danger mt-2">
                                <ul className="mb-0">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StrategyTemplateModal;
